import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Spinner, Badge } from '../atoms';
import { useEntityPermission } from '../lib/usePermissions';
import {
  groupsApi, GROUPS_QK, Group, GroupItem,
  GROUPABLE_TYPE_LABEL, GROUPABLE_TYPE_ROUTE,
} from '../lib/groups';
import { GroupCustomFields } from '../organisms';

/**
 * GroupDetail — a single group: rename/delete, the objects in it (grouped by
 * type, each linking to its record, with remove), and the group's own custom
 * fields. The objects ARE what's in the group; removing one just unlinks it (the
 * record itself is never touched). Internal only (spec FR-7); the whole route is
 * gated on the `group` entity by RequireEntity in main.tsx.
 */
export function GroupDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = useEntityPermission('group', 'can_edit');
  const canDelete = useEntityPermission('group', 'can_delete');

  const groupQ = useQuery<Group>({
    queryKey: GROUPS_QK.one(id),
    queryFn: ({ signal }) => api.get<Group>(`/api/groups/${id}`, signal),
    enabled: !!id,
  });
  const itemsQ = useQuery({
    queryKey: GROUPS_QK.items(id),
    queryFn: ({ signal }) => groupsApi.items(id, signal),
    enabled: !!id,
  });

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');

  const renameMut = useMutation({
    mutationFn: () => groupsApi.rename(id, { name: name.trim(), description: desc }),
    onSuccess: () => { setEditing(false); qc.invalidateQueries({ queryKey: GROUPS_QK.one(id) }); qc.invalidateQueries({ queryKey: GROUPS_QK.all }); },
  });
  const removeMut = useMutation({
    mutationFn: () => groupsApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: GROUPS_QK.all }); navigate('/groups'); },
  });
  const removeItemMut = useMutation({
    mutationFn: (it: GroupItem) => groupsApi.removeItem(id, it.entity_type, it.entity_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_QK.items(id) }),
  });

  const items = itemsQ.data?.items ?? [];
  const byType = useMemo(() => {
    const m = new Map<string, GroupItem[]>();
    for (const it of items) { if (!m.has(it.entity_type)) m.set(it.entity_type, []); m.get(it.entity_type)!.push(it); }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  if (groupQ.isLoading) return <div style={{ padding: 'var(--sp-5)' }}><Spinner /></div>;
  if (groupQ.error || !groupQ.data) {
    return (
      <div style={{ padding: 'var(--sp-5)' }}>
        <p style={{ color: 'var(--color-ink-3)' }}>This group could not be found.</p>
        <Link to="/groups">← Back to Groups</Link>
      </div>
    );
  }
  const group = groupQ.data;

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: 'var(--sp-5) var(--sp-4) var(--sp-6)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
        <Link to="/groups" style={{ color: 'var(--color-ink-3)' }}>Groups</Link> › <span style={{ color: 'var(--color-ink)' }}>{group.name}</span>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {!editing ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 'var(--fs-2xl, 26px)', color: 'var(--color-ink)' }}>{group.name}</h1>
              {group.is_system && <Badge variant="info">Default</Badge>}
            </div>
            {group.description && <p style={{ margin: '4px 0 0', color: 'var(--color-ink-2, var(--color-ink-3))' }}>{group.description}</p>}
            <p style={{ margin: '6px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>{items.length} object{items.length === 1 ? '' : 's'} in this group</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', flex: 1, minWidth: 260 }}>
            <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Group name"
              style={{ padding: '8px 10px', fontSize: 'var(--fs-lg, 18px)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md,8px)', background: 'var(--color-card)', color: 'var(--color-ink)' }} />
            <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" aria-label="Group description"
              style={{ padding: '8px 10px', fontSize: 'var(--fs-sm)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md,8px)', background: 'var(--color-card)', color: 'var(--color-ink)' }} />
          </div>
        )}
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          {canEdit && !editing && !group.is_system && <Button size="sm" variant="secondary" onClick={() => { setName(group.name); setDesc(group.description || ''); setEditing(true); }}>Rename</Button>}
          {editing && <Button size="sm" variant="primary" disabled={!name.trim() || renameMut.isPending} onClick={() => renameMut.mutate()}>Save</Button>}
          {editing && <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>}
          {canDelete && !editing && !group.is_system && (
            <Button size="sm" variant="danger" disabled={removeMut.isPending}
              onClick={() => { if (window.confirm(`Delete the group "${group.name}"? The records in it are not deleted — they're just no longer grouped.`)) removeMut.mutate(); }}>
              Delete
            </Button>
          )}
          {(canEdit || canDelete) && !editing && group.is_system && (
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Default group — managed in Org&nbsp;Settings</span>
          )}
        </div>
      </div>

      {/* Objects in this group */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--fs-lg, 18px)', color: 'var(--color-ink)' }}>In this group</h2>
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          Add records to this group from each record's own <b>Groups</b> tab. You see only the records you're allowed to.
        </p>
        {itemsQ.isLoading ? <Spinner size="sm" /> : items.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Nothing is in this group yet.</p>
        ) : (
          byType.map(([type, list]) => (
            <div key={type} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
                <Badge variant="neutral">{GROUPABLE_TYPE_LABEL[type] || type}</Badge>
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>{list.length}</span>
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {list.map((it) => {
                  const singular = GROUPABLE_TYPE_LABEL[type]?.replace(/s$/, '') || type;
                  // Server resolves the object's name/number; fall back to type + a short
                  // id only when it can't (e.g. a since-deleted object still linked).
                  const display = it.label || `${singular} · ${it.entity_id.slice(0, 8)}`;
                  const route = GROUPABLE_TYPE_ROUTE[type];
                  return (
                  <li key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md,8px)', background: 'var(--color-card)' }}>
                    {route ? (
                      <Link to={`${route}/${it.entity_id}`} style={{ fontSize: 'var(--fs-sm)', overflowWrap: 'anywhere' }}>{display}</Link>
                    ) : (
                      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', overflowWrap: 'anywhere' }}>{display}</span>
                    )}
                    {canEdit && (
                      <button type="button" aria-label="Remove from group" title="Remove from group" onClick={() => removeItemMut.mutate(it)} disabled={removeItemMut.isPending}
                        style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--color-ink-3)', fontSize: '18px', lineHeight: 1, flexShrink: 0 }}>×</button>
                    )}
                  </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </section>

      {/* The group's own custom fields */}
      <GroupCustomFields groupId={id} canEdit={canEdit} />
    </div>
  );
}

